import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import SEO from "../../components/seo/SEO";
import Header from "../../components/layouts/Header/Header";
import AutoResizeTextArea from "../../components/ui/AutoResizeTextarea/AutosizeTextarea";
import ScoreDisplay from "../Day/features/tiles/planning/components/ScoreDisplay";
import ScoreSlider from "../Day/features/tiles/planning/components/ScoreSlider";
import TestFooter from "../../components/layouts/Footer/TestFooter";
import FAQ from "../../components/ui/FAQ/FAQ";
import { client } from "../../services/client";
import "./landingpage.css";
import Demo from "./components/Demo";
import HowItWorksLoop from "./components/HowItWorksLoop";

const PENDING_DAY_KEY = "nwd_pending_day";

const EMPTY_DAY = {
  startScore: 0,
  endScore: 0,
  thankYou: "",
  selfLess: "",
  mindBody: "",
  momentOne: "",
  momentOneScore: 0,
  momentTwo: "",
  momentTwoScore: 0,
  momentThree: "",
  momentThreeScore: 0,
  momentFour: "",
  momentFourScore: 0,
  momentFive: "",
  momentFiveScore: 0,
  rememberToday: "",
  leaveBehind: "",
  onMyMind: "",
};

// User testimony flipcards, rendered beside the hero headline. Images come
// from the LP_US_* Sanity photos (userStoryImages[i]).
const USER_STORIES = [
  {
    num: "Richard's Story",
    title: "Improved self-awareness & mental resilience.",
    body: '"My ability to react and respond to life in a more connected yet less attached way has sky-rocketed since I started using NoWastedDays. I understand the shape of my mind more and use this to respond better to shifts day-to-day that would previously have thrown me into a bit of a spin."',
  },
  {
    num: "Eve's Story",
    title: "Improved self-assurance.",
    body: '"Whilst it seems like a small thing, using the journal to repeatedly remind myself why I am proud to live in my mind and body everyday really does build greater self-assurance. Over time, alongside the self-awareness focus, using NoWastedDays has made me a lot clearer and kinder with myself."',
  },
];

const WAYS_OF_LOOKING = [
  "How to spot signals for truth and insight amongst all the noise.",
  "How not to be tricked by the data you see on the platform or in the real world.",
  "How to quickly dissect any situation to see it more clearly.",
  "How to keep your mind open for longer to come up with more creative solutions.",
  "How to be an optimist and a sceptic at the same time.",
  "How your vulnerability may well be your biggest strength.",
];

function fmtScore(v) {
  if (!v || v <= 0) return "—";
  const s = v.toFixed(1);
  return s.endsWith(".0") ? s.slice(0, -2) : s;
}

function sliderBg(value, max) {
  const pct = max ? (value / max) * 100 : 0;
  return `linear-gradient(to right, rgba(0, 74, 35, 0.45) ${pct}%, rgba(0, 74, 35, 0.05) ${pct}%)`;
}

function LandingScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [logDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [today, setToday] = useState(EMPTY_DAY);

  const handleTodayChange = (e) => {
    const { name, value, type } = e.target;
    setToday((prev) => ({
      ...prev,
      [name]: type === "range" ? parseFloat(value) : value,
    }));
  };

  // Mirrors DayScreen.deleteTodayKey: clears a moment's text & score when the
  // trash icon (rendered by ScoreDisplay) is clicked.
  const deleteTodayKey = (e) => {
    const name = e.currentTarget?.dataset?.name;
    if (!name) return;
    setToday((prev) => ({
      ...prev,
      [name]: "",
      [`${name}Score`]: 0,
    }));
  };

  const hasInteracted = useMemo(() => {
    return Object.entries(today).some(([k, v]) => {
      if (typeof v === "number") return v > 0;
      if (typeof v === "string") return v.trim().length > 0;
      return false;
    });
  }, [today]);

  const handleSaveDay = () => {
    const payload = {
      ...today,
      logDate,
    };
    try {
      localStorage.setItem(PENDING_DAY_KEY, JSON.stringify(payload));
    } catch (e) {}
    navigate("/register");
  };

  // Convenience accessors for the existing summary/delta logic
  const startScore = today.startScore;
  const endScore = today.endScore;
  const momentScores = [
    today.momentOneScore,
    today.momentTwoScore,
    today.momentThreeScore,
    today.momentFourScore,
    today.momentFiveScore,
  ];

  const yearGridCells = useMemo(() => {
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const intensity = Math.min(1, (i / 42) * 1.4 + Math.random() * 0.25);
      cells.push(+(0.06 + intensity * 0.55).toFixed(3));
    }
    return cells;
  }, []);

  const [benefitImages, setBenefitImages] = useState([]);
  const [userStoryImages, setUserStoryImages] = useState([]);
  const [howItWorksImages, setHowItWorksImages] = useState([]);
  const [wolImages, setWolImages] = useState([]);
  const [flippedBenefit, setFlippedBenefit] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const wolTrackRef = useRef(null);

  const scrollWol = (direction) => {
    const el = wolTrackRef.current;
    if (!el) return;
    const card = el.querySelector(".wol-card");
    const step = card ? card.offsetWidth + 24 : el.offsetWidth * 0.8;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const t = setTimeout(tryScroll, 80);
    return () => clearTimeout(t);
  }, [location.hash]);

  useEffect(() => {
    if (!lightboxImage) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxImage(null);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxImage]);

  useEffect(() => {
    let cancelled = false;
    const fetchLandingImages = async () => {
      const photoProjection = `{
        _id,
        title,
        "imageUrl": imageFile.asset->url,
        altText,
        caption,
        description
      }`;
      try {
        const [benefits, userStories, howItWorks, wol] = await Promise.all([
          client.fetch(
            `*[_type == "photo" && title match "LP_BENEFITS_*"] | order(title asc)${photoProjection}`,
          ),
          client.fetch(
            `*[_type == "photo" && title match "LP_US_*"] | order(title asc)${photoProjection}`,
          ),
          client.fetch(
            `*[_type == "photo" && title match "LP_HOWITWORKS_*"] | order(title asc)${photoProjection}`,
          ),
          client.fetch(
            `*[_type == "photo" && title match "LP_WOS_*"] | order(title asc)${photoProjection}`,
          ),
        ]);
        if (cancelled) return;
        if (benefits) setBenefitImages(benefits);
        if (userStories) setUserStoryImages(userStories);
        if (howItWorks) setHowItWorksImages(howItWorks);
        if (wol) setWolImages(wol);
      } catch (error) {
        console.error("Error fetching landing page images:", error);
      }
    };
    fetchLandingImages();
    return () => {
      cancelled = true;
    };
  }, []);

  const smoothScroll = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const getDelta = () => {
    if (startScore > 0 && endScore > 0) {
      const d = endScore - startScore;
      const sign = d > 0 ? "+" : "";
      const text = sign + d.toFixed(1).replace(/\.0$/, "");
      if (d > 0.05)
        return {
          text,
          cls: "delta-pos",
          sub: "A day that gave more than it took.",
        };
      if (d < -0.05)
        return {
          text,
          cls: "delta-neg",
          sub: "A day worth noting. Worth leaving behind.",
        };
      return {
        text: sign + "0",
        cls: "delta-zero",
        sub: "A steady day. Steadiness counts.",
      };
    }
    return {
      text: "·",
      cls: "delta-zero",
      sub: "Move both sliders to see your day's shift.",
    };
  };
  const delta = getDelta();

  return (
    <>
      <SEO
        title="NoWastedDays — Your self-awareness platform"
        description="A self-awareness platform built around a single, lightweight ritual: prime the day, plan what matters, then reflect on what actually happened."
        canonicalUrl="https://www.nowasteddays.io"
      />
      <div className="lp-root">
        {/* NAV */}
        {/* <header className="nav">
          <div className="brand">
            <div className="mark">N</div>
            <span>NoWastedDays</span>
          </div>
          <nav className="nav-links">
            <a href="#how" onClick={(e) => smoothScroll(e, "how")}>
              How it works
            </a>
            <a href="#benefits" onClick={(e) => smoothScroll(e, "benefits")}>
              Benefits
            </a>
            <a href="#demo" onClick={(e) => smoothScroll(e, "demo")}>
              Try it
            </a>
            <Link to="/register" className="cta">
              Start journaling
            </Link>
          </nav>
        </header> */}
        <Header variant="light" active={true} />

        {/* HERO */}
        <section className="hero">
          <div className="hero-inner">
            <div>
              {/* <span className="eyebrow">NoWastedDays.io</span> */}

              <h2 className="lede">
                NoWastedDays is a journal designed to do more than make you more
                mindful and productive. <br></br>
                <br></br>
                It is designed to help you learn about yourself and translate
                this knowledge into greater health and happiness.
              </h2>
              <div className="hero-cta">
                {/* <a
                  href="#how"
                  className="btn btn-primary"
                  onClick={(e) => smoothScroll(e, "how")}
                >
                  How does it work?
                </a> */}

                {/* <a
                  href="#benefits"
                  className="btn btn-ghost"
                  onClick={(e) => smoothScroll(e, "benefits")}
                >
                  Hear from users.
                </a> */}
              </div>
            </div>

            {/* hero-testimonies — user story flipcards beside the headline */}
            <div className="hero-testimonies">
              {USER_STORIES.map((b, i) => {
                const imgUrl = userStoryImages[i]?.imageUrl || null;
                const altText = userStoryImages[i]?.altText || b.title;
                const isFlipped = flippedBenefit === i;
                return (
                  <article
                    key={b.num}
                    className={`benefit flipcard${
                      isFlipped ? " is-flipped" : ""
                    }`}
                    onMouseEnter={() => setFlippedBenefit(i)}
                    onMouseLeave={() => setFlippedBenefit(null)}
                    onClick={() => setFlippedBenefit(isFlipped ? null : i)}
                    aria-label={`${b.title} — tap to read more`}
                  >
                    <div className="flipcard-inner">
                      <div className="flipcard-face flipcard-front">
                        {imgUrl && (
                          <div
                            className="flipcard-img"
                            style={{ backgroundImage: `url(${imgUrl})` }}
                            role="img"
                            aria-label={altText}
                          />
                        )}
                        <div className="flipcard-front-text">
                          <div className="num">{b.num}</div>
                          <h3>{b.title}</h3>
                          <span className="flipcard-hint">
                            Hover to read more
                          </span>
                        </div>
                      </div>
                      <div className="flipcard-face flipcard-back">
                        <div className="num">{b.num}</div>
                        <h3>{b.title}</h3>
                        <p>{b.body}</p>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          {/* hero-visual — "How it works" signal loop */}
          <section id="how">
            <HowItWorksLoop />
          </section>
        </section>

        {/* STATEMENT */}
        <section className="statement">
          <div className="statement-inner">
            <h2>
              Self-awareness is arguably the skill that provides the greatest
              support to a happy, healthy mind, however, our brains do not find
              it easy to do.
              <br />
              <br />
              Data and technology could have combined to solve this problem a
              long time ago, but instead it has been used to make our
              self-awareness even harder to achieve.
              <br />
              <br />
              This is exactly why NoWastedDays exists; to provide anyone the{" "}
              quiet, productive and supportive digital space to nurture and
              apply their self-awareness every single day.
            </h2>
          </div>
        </section>

        {/* WAYS OF LOOKING */}
        {/* <section className="ways-of-looking" id="ways-of-looking">
          <div className="wol-inner">
            <div className="wol-head">
              <p className="lp-lede-note">
                What if I think seeing or interpreting my explore page will be
                difficult?
              </p>
              <p>
                Looking at data that describes how you may have felt, how much
                you did and where your plans differed from your actions can be
                hard. To provide anyone with the toolbox of skills required to
                make the most of their data as well as radically reduce any
                potentially triggering effects of seeing it we have created{" "}
                <strong>Ways of Looking</strong>: a unique learning journey to
                help you explore your data with maximum calmness, creativity and
                confidence.
              </p>
              <p className="wol-subhead">Things you will pick up include:</p>
            </div>

            <div className="wol-carousel">
              <button
                type="button"
                className="wol-arrow wol-arrow-prev"
                onClick={() => scrollWol(-1)}
                aria-label="Previous insight"
              >
                ‹
              </button>
              <button
                type="button"
                className="wol-arrow wol-arrow-next"
                onClick={() => scrollWol(1)}
                aria-label="Next insight"
              >
                ›
              </button>
              <div className="wol-track" ref={wolTrackRef}>
                {WAYS_OF_LOOKING.map((title, i) => {
                  const img = wolImages[i];
                  const hasImage = Boolean(img?.imageUrl);
                  const altText = img?.altText || title;
                  return (
                    <div key={i} className="wol-card">
                      <button
                        type="button"
                        className="wol-card-img lp-zoomable"
                        style={
                          hasImage
                            ? { backgroundImage: `url(${img.imageUrl})` }
                            : undefined
                        }
                        onClick={() =>
                          hasImage &&
                          setLightboxImage({
                            url: img.imageUrl,
                            alt: altText,
                          })
                        }
                        disabled={!hasImage}
                        aria-label={
                          hasImage ? `${altText} — click to enlarge` : altText
                        }
                      >
                        {hasImage && (
                          <span className="lp-zoom-pill" aria-hidden="true">
                            ⤢
                          </span>
                        )}
                      </button>
                      <div className="wol-card-body">
                        <div className="wol-card-num">
                          {String(i + 1).padStart(2, "0")}
                        </div>
                        <p>{title}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section> */}

        {/* FAQs */}
        <section className="lp-faqs" id="faqs">
          <div className="lp-faqs-inner">
            <div className="lp-faqs-head">
              <h2 className="lp-faqs-heading">Frequently Asked Questions</h2>
            </div>
            <FAQ variant="cards" />
          </div>
        </section>

        {/* RHYTHM */}
        {/* <section className="rhythm">
          <div className="rhythm-inner">
            <div>
              <h2>Days become weeks. Weeks become a year you can read.</h2>
              <p>
                NoWastedDays quietly accumulates. After a month, a year, you
                don't get a dashboard — you get a record of attention. The
                ordinary days are the ones you're glad you wrote down.
              </p>
              <a
                className="btn btn-primary"
                href="#demo"
                onClick={(e) => smoothScroll(e, "demo")}
              >
                Start your first page
              </a>
              <div className="year-grid" aria-hidden="true">
                {yearGridCells.map((alpha, i) => (
                  <div
                    key={i}
                    className="day"
                    style={{ background: `rgba(0, 74, 35, ${alpha})` }}
                  />
                ))}
              </div>
            </div>
            <div className="quote">
              Five minutes in the morning, five in the evening. It's the most
              consistent thing I do — and the only thing that's made the rest of
              my life feel more consistent.
              <span className="who">— Sam, 18-month user</span>
            </div>
          </div>
        </section> */}

        {lightboxImage && (
          <div
            className="lp-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={lightboxImage.alt}
            onClick={() => setLightboxImage(null)}
          >
            <button
              type="button"
              className="lp-lightbox-close"
              onClick={() => setLightboxImage(null)}
              aria-label="Close enlarged image"
            >
              ×
            </button>
            <img
              src={lightboxImage.url}
              alt={lightboxImage.alt}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* FOOTER */}
        <TestFooter />
      </div>
    </>
  );
}

export default LandingScreen;
